import {Component, EventEmitter, HostListener, OnDestroy, OnInit, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {Subscription, timer} from 'rxjs';
import {ConstantsService} from '../fidelidade/ConstantsService';
import {MonitoradorPedidos} from '../fidelidade/MonitoradorPedidos';
import {FidelidadeComponent} from '../fidelidade/fidelidade.component';
import {AutorizacaoService} from '../services/autorizacao.service';
import {RotaGuardClient} from '../guards/auth-loja.guard';
import {EnumFiltroDePedidos, PedidosService} from '../services/pedidos.service';
import {VendasPedidoService} from '../services/vendas-pedido.service';
import {CaixaService} from '../pdv/services/caixa.service';
import {MesaService} from '../services/mesa.service';
import {EmpresasService} from '../superadmin/services/empresas.service';
import {ServidorImpressaoExecutorService} from '../services/servidor-impressao-executor.service';
import {FuncaoMenuV2} from '../componentes/menu-v2/menu-v2.catalogo';
import {criarCatalogoParaUsuario} from '../componentes/menu-v2/menu-v2.contexto';
import {UsoMenuV2} from '../componentes/menu-v2/menu-v2.uso';
import {apliqueFaseDoOnboarding} from '../componentes/menu-v2/menu-v2.fases';
import {EnumStatusPedido} from '../../../server/lib/emun/EnumStatusPedido';
import {EnumMomentoImprimirAuto} from '../../../server/lib/emun/EnumMomentoImprimirAuto';
import {TelaImprimirPedido} from '../fidelidade/TelaImprimirPedido';
import {ImpressaoService} from '../services/impressao.service';
import {NfceService} from '../services/nfce.service';
import {ContatosService} from '../services/contatos.service';
import {EntregadorService} from '../services/entregador.service';
import {
  abaDaEtapa, AbaComanda, CODIGO_DO_STATUS, EtapaComanda, ordeneParaOTrilho, PassoComanda, perguntaPagamento,
  proximoPasso, statusPermitidos
} from './home-celular.comandas';

/** O momento do dia decide o que a tela mostra primeiro; a permissão decide se cada bloco existe. */
export type MomentoHomeCelular = 'contaNova' | 'antes' | 'aberta' | 'fechou';

/** Uma comanda da lista: um pedido em aberto com o próximo passo, ou uma pendência agregada. */
export interface ComandaHomeCelular {
  id: string;
  etapa: EtapaComanda | null;
  aba: AbaComanda;
  /** Cor da comanda: a etapa, "atraso" por cima dela, ou "pendencia". */
  tom: string;
  rotulo: string;
  icone: string;
  titulo: string;
  meta: string;
  nota: string;
  valor: number | null;
  tempo: string;
  quente: boolean;
  itens: Array<{qtde: string, descricao: string, observacao: string}>;
  maisItens: number;
  /** null = sem passo permitido: o botão principal abre o pedido. */
  passo: PassoComanda | null;
  rota: string;
  pedido?: any;
}

/** Pedido em aberto há mais que isso não é "agora": é esquecido, e sai da lista para um aviso à parte. */
const MINUTOS_PEDIDO_ANTIGO = 12 * 60;
const LISTA_INICIAL = 3;
const LISTA_PASSO = 5;

/** Cor de reconhecimento por área do menu: pastel no fundo, ícone na cor forte. */
const COR_DO_GRUPO: {[grupo: string]: string} = {
  operacao: 'azul', cardapio: 'ceu', clientes: 'rosa', atendimento: 'roxo',
  gestao: 'verde', pay: 'teal', configuracoes: 'cinza', conta: 'cinza'
};

/** As portas de venda, na ordem em que aparecem; cada uma só existe se o catálogo a liberar. */
const PORTAS_DE_VENDA = [
  {id: 'pedidos/novo-v3', nome: 'Pedido', detalhe: 'entrega ou retirada', icone: 'fe-truck', cor: 'azul'},
  {id: 'pedidos/pdv/novo', nome: 'Balcão', detalhe: 'venda rápida', icone: 'fe-shopping-bag', cor: 'teal'},
  {id: 'pedidos-mesas', nome: 'Mesa', detalhe: 'abre a comanda', icone: 'fe-grid', cor: 'roxo'}
];

const DIAS_DA_SEMANA = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

/**
 * Início pensado para o dono que só tem o celular. Não reaproveita os blocos da
 * home v2: a tela muda com o momento do dia (conta nova, antes de abrir, aberta,
 * depois de fechar) e abre pelo que está esperando o dono agora.
 *
 * Permissões: cada bloco checa a sua — vendas só com a operação de relatórios,
 * caixa com a de caixa, pausar com a de gerenciar loja, mesas e pedidos pelo
 * módulo. Vender, atalhos e "Todas as funções" saem do mesmo catálogo do
 * menu-v2, que já filtra por permissão, módulo e recurso.
 */
@Component({
  selector: 'app-home-celular',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './home-celular.component.html',
  styleUrls: ['./home-celular.component.scss']
})
export class HomeCelularComponent implements OnInit, OnDestroy {
  /** O dono pediu a home completa: o switch troca a tela até o fim da sessão. */
  @Output() verHomeCompleta = new EventEmitter<void>();

  usuario: any = {};
  empresa: any = null;
  temModuloPedidos = false;
  temModuloMesas = false;
  exibirFinanceiro = false;
  podeOperarCaixa = false;
  podeGerenciarLoja = false;

  filaAgora: any = null;
  pedidosAbertos: any[] = [];
  agendados: any[] = [];
  hoje: any = null;
  anterior: any = null;
  horas: number[] = [];
  caixa: any;
  mesas: any;

  funcoes: FuncaoMenuV2[] = [];
  readonly uso = new UsoMenuV2(UsoMenuV2.armazenamentoLocal());

  folha: '' | 'qr' | 'mais' | 'entregador' | 'pagamento' = '';
  abaAtual: AbaComanda = 'todos';
  podeAlterarStatus = false;
  podeCancelarPedido = false;
  /** Id do pedido com ação em andamento: trava os botões dele. */
  processando: any = null;
  pedidoEmAcao: any = null;
  etapaEmAcao: EtapaComanda | null = null;
  statusEmAcao: string | null = null;
  escolhendoFormaDoSaldo = false;
  confirmandoRecusa = false;
  entregadores: any[] | null = null;
  /** A lista de pedidos não traz os itens; a home busca por pedido, como a tela de pedidos faz ao abrir um. */
  private itensPorPedido: {[id: string]: any[]} = {};
  private buscandoItens = new Set<string>();
  aviso = '';
  reabrindo = false;
  /** Quantas comandas a lista mostra antes do "Ver mais". */
  limiteLista = LISTA_INICIAL;
  qrErro = false;

  private comandasEmCache: ComandaHomeCelular[] = [];
  private assinaturaComandas = '';
  private contagemNovos = -1;
  private timerAviso: any;
  private inscricoes: Subscription[] = [];

  constructor(private router: Router, private constants: ConstantsService, private monitorador: MonitoradorPedidos,
              private autorizacao: AutorizacaoService, private pedidosService: PedidosService,
              private vendasPedidoService: VendasPedidoService, private caixaService: CaixaService,
              private mesaService: MesaService, private empresasService: EmpresasService,
              private servidorImpressao: ServidorImpressaoExecutorService, private casca: FidelidadeComponent,
              private impressao: ImpressaoService, private nfce: NfceService, private contatosService: ContatosService,
              private entregadorService: EntregadorService) {}

  ngOnInit(): void {
    this.usuario = this.autorizacao.getUser() || {};

    // Garçom não tem Início: vai direto para o salão, como na home de sempre.
    if (this.usuario.garcom) {
      this.router.navigate(['/admin/pedidos-mesas']).then(() => {});
      return;
    }

    this.exibirFinanceiro = RotaGuardClient.visualizaRelatorios(this.usuario);
    this.podeOperarCaixa = RotaGuardClient.operarCaixa(this.usuario);
    this.podeGerenciarLoja = RotaGuardClient.gerenciaLoja(this.usuario);
    // Mesmas travas do mudar-status: mudar status e recusar têm permissão própria.
    this.podeAlterarStatus = RotaGuardClient.alterarStatusPedido(this.usuario);
    this.podeCancelarPedido = RotaGuardClient.cancelarPedido(this.usuario);

    this.inscricoes.push(this.constants.empresa$.subscribe((empresa: any) => {
      if (!empresa) return;
      this.empresa = empresa;
      this.monteCatalogo();
      this.carregueMesas();
    }));

    this.inscricoes.push(this.constants.moduloPedido$.subscribe((tem: any) => {
      const tinha = this.temModuloPedidos;
      this.temModuloPedidos = !!tem;
      if (this.temModuloPedidos && !tinha) this.carreguePedidos();
    }));

    this.inscricoes.push(this.constants.moduloMesasGarcom$.subscribe((tem: any) => {
      const tinha = this.temModuloMesas;
      this.temModuloMesas = !!tem;
      if (this.temModuloMesas && !tinha) this.carregueMesas();
    }));

    // O monitorador já consulta o servidor sozinho: só recarrego quando o placar dele muda.
    this.inscricoes.push(this.monitorador.resumoPedidos$.subscribe(() => {
      const novos = (this.monitorador.novosPedidos || []).length;
      if (novos === this.contagemNovos) return;
      this.contagemNovos = novos;
      this.carreguePedidos();
    }));

    // O modal de pausar é da casca; quando ele fecha a loja, o estado daqui acompanha.
    this.inscricoes.push(this.casca.lojaFechada.subscribe((resultado: any) => {
      if (resultado && resultado.fechou) this.recarregueEmpresa();
    }));

    this.carregueCaixa();
    this.carregueHoje();

    this.inscricoes.push(timer(60000, 60000).subscribe(() => {
      this.carreguePedidos();
      this.carregueCaixa();
      this.carregueMesas();
    }));
    this.inscricoes.push(timer(120000, 120000).subscribe(() => this.carregueHoje()));
  }

  ngOnDestroy(): void {
    this.inscricoes.forEach((inscricao: Subscription) => inscricao.unsubscribe());
    if (this.timerAviso) clearTimeout(this.timerAviso);
  }

  // ─────────────────────────── Carga ───────────────────────────

  carreguePedidos(): void {
    if (!this.temModuloPedidos) return;

    this.pedidosService.obtenhaResumoStatus().then((fila: any) => this.filaAgora = fila || null).catch(() => {});

    // O mesmo universo da aba "Em aberto" da tela de pedidos (sem comandas de mesa).
    this.pedidosService.listePedidos(0, 100, {emAberto: true}, EnumFiltroDePedidos.pedidos)
      .then((resposta: any) => {
        this.pedidosAbertos = (resposta && resposta.pedidos) || [];
        this.carregueItens();
      })
      .catch(() => {});

    this.pedidosService.listePedidos(0, 20, {emAberto: true}, EnumFiltroDePedidos.agendados)
      .then((resposta: any) => this.agendados = ((resposta && resposta.pedidos) || [])
        .filter((pedido: any) => this.ehHoje(pedido.horarioEntregaAgendada)))
      .catch(() => {});
  }

  /**
   * Hoje contra o mesmo dia da semana passada, até a mesma hora: restaurante
   * vende por dia da semana, e comparar a sexta com a quinta acusa queda à toa.
   */
  carregueHoje(): void {
    if (!this.exibirFinanceiro) return;

    const agora = new Date();
    const hoje = this.diaNoFiltro(agora);
    const semanaPassada = this.diaNoFiltro(new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() - 7));
    const hora = agora.toTimeString().substr(0, 8);
    const filtro = {dl: '1', bl: '1', ms: '1'};

    this.vendasPedidoService.obtenhaResumo({...filtro, di: hoje, df: hoje})
      .then((resumo: any) => this.hoje = resumo || {qtde: 0, total: 0}).catch(() => {});
    this.vendasPedidoService.obtenhaResumo({...filtro, di: semanaPassada, df: `${semanaPassada} ${hora}`})
      .then((resumo: any) => this.anterior = resumo || null).catch(() => this.anterior = null);
    this.vendasPedidoService.obtenhaResumoHoras({...filtro, di: hoje, df: hoje})
      .then((resposta: any) => this.horas = ((resposta && resposta.horas) || []).map((item: any) => Number(item.total) || 0))
      .catch(() => this.horas = []);
  }

  carregueCaixa(): void {
    if (!this.podeOperarCaixa) return;

    this.caixaService.obtenhaCaixaAtual().then((resposta: any) => {
      const caixa = resposta && resposta.caixa;
      this.caixa = caixa ? {aberto: true, desde: this.hora(caixa.horarioAbertura),
        operador: caixa.operador && caixa.operador.nome ? String(caixa.operador.nome).split(' ')[0] : ''} : {aberto: false};
    }).catch(() => this.caixa = undefined);
  }

  carregueMesas(): void {
    if (!this.temModuloMesas || !this.empresa) return;

    this.mesaService.obtenhaMesas(this.empresa).then((mesas: any[]) => {
      const lista = (mesas || []).filter((mesa: any) => !mesa.removida);
      const ocupadas = lista.filter((mesa: any) => mesa.comandas && mesa.comandas.length);
      this.mesas = {total: lista.length, ocupadas: ocupadas.length};
    }).catch(() => this.mesas = null);
  }

  private recarregueEmpresa(): void {
    this.empresasService.logada().then((empresa: any) => {
      if (!empresa || !this.empresa) return;
      this.empresa.fechadoTemporariamente = empresa.fechadoTemporariamente;
      this.empresa.estaAberta = empresa.estaAberta;
      this.empresa.estaRecebendoPedidos = empresa.estaRecebendoPedidos;
      this.empresa.mensagemPausaProgramada = empresa.mensagemPausaProgramada;
      this.empresa.horariosDoServico = empresa.horariosDoServico;
    }).catch(() => {});
  }

  /** Mesmo catálogo e mesma fase do menu: o que o menu guarda para depois, aqui só aparece na busca. */
  private monteCatalogo(): void {
    const catalogo = criarCatalogoParaUsuario(this.usuario, this.empresa, this.constants, this.servidorImpressao.ehUmServidorAtivo());
    this.funcoes = apliqueFaseDoOnboarding(catalogo, (this.empresa && this.empresa.onboarding) || null);
  }

  // ─────────────────────────── Momento do dia ───────────────────────────

  private get servico(): any {
    return ((this.empresa && this.empresa.horariosDoServico) || [])[0];
  }

  get estadoLoja(): 'aberta' | 'pausada' | 'fechada' {
    if (!this.empresa) return 'fechada';
    if (this.empresa.fechadoTemporariamente) return 'pausada';

    const servico = this.servico;
    const aberta = servico ? servico.estaAberta && servico.estaRecebendoPedidos
      : this.empresa.estaAberta && this.empresa.estaRecebendoPedidos;

    return aberta ? 'aberta' : 'fechada';
  }

  /** Só depois que a fila respondeu: antes disso a tela segue o estado da loja, sem pular. */
  get contaNova(): boolean {
    return this.temModuloPedidos && !!this.filaAgora && this.filaAgora.jaVendeu === false;
  }

  get momento(): MomentoHomeCelular {
    if (this.contaNova) return 'contaNova';
    if (this.estadoLoja !== 'fechada') return 'aberta';

    return this.proximaAbertura ? 'antes' : 'fechou';
  }

  /** Próximo turno de hoje que ainda não começou ("18:00"), ou null. */
  get proximaAbertura(): string | null {
    const agora = this.minutosDoDia(new Date());

    for (const turno of ((this.servico && this.servico.horariosHoje) || [])) {
      if (!turno.funciona || !turno.horarioAbertura) continue;
      if (this.minutosDaHora(turno.horarioAbertura) > agora) return String(turno.horarioAbertura).substr(0, 5);
    }

    return null;
  }

  get faltaParaAbrir(): string {
    const abertura = this.proximaAbertura;
    if (!abertura) return '';

    const minutos = this.minutosDaHora(abertura) - this.minutosDoDia(new Date());
    const horas = Math.floor(minutos / 60), resto = minutos % 60;

    return horas ? `daqui a ${horas}h${resto < 10 ? '0' : ''}${resto}` : `daqui a ${resto} min`;
  }

  /** Fração do intervalo até abrir já percorrida, para o anel do "Abre às". */
  get progressoAbertura(): number {
    const abertura = this.proximaAbertura;
    if (!abertura) return 0;

    const falta = this.minutosDaHora(abertura) - this.minutosDoDia(new Date());

    return Math.max(0.05, Math.min(1, 1 - falta / 240));
  }

  get fechamentoDeHoje(): string {
    const agora = this.minutosDoDia(new Date());

    for (const turno of ((this.servico && this.servico.horariosHoje) || [])) {
      if (!turno.funciona || !turno.horarioAbertura || !turno.horarioFechamento) continue;
      const abre = this.minutosDaHora(turno.horarioAbertura);
      let fecha = this.minutosDaHora(turno.horarioFechamento);
      if (fecha <= abre) fecha += 24 * 60;
      if (agora >= abre && agora < fecha) return String(turno.horarioFechamento).substr(0, 5);
    }

    return '';
  }

  get textoEstadoLoja(): string {
    if (this.estadoLoja === 'pausada') return this.empresa.mensagemPausaProgramada || 'Pausada. Reabra para voltar a receber pedidos.';
    if (this.estadoLoja === 'aberta') return this.fechamentoDeHoje ? `Recebendo pedidos até ${this.fechamentoDeHoje}` : 'Recebendo pedidos';

    const servico = this.servico;
    return (servico && (servico.mensagemAbrirPedidos || servico.motivo)) || 'Fora do horário de funcionamento';
  }

  // ─────────────────────────── Vendas de hoje ───────────────────────────

  get exibirHoje(): boolean {
    return this.exibirFinanceiro && this.temModuloPedidos && !!this.hoje;
  }

  get totalHoje(): number {
    return Number(this.hoje && (this.hoje.totalComTaxa || this.hoje.total)) || 0;
  }

  get pedidosHoje(): number {
    return Number(this.hoje && this.hoje.qtde) || 0;
  }

  get ticketHoje(): number {
    return Number(this.hoje && this.hoje.ticketMedio) || 0;
  }

  get taxasHoje(): number {
    return Number(this.hoje && this.hoje.taxaDeEntrega) || 0;
  }

  /** "12% a mais" / "8% a menos" / "" quando não há base para comparar. */
  get variacaoHoje(): {texto: string, sobe: boolean} | null {
    const antes = Number(this.anterior && (this.anterior.totalComTaxa || this.anterior.total)) || 0;
    if (!antes) return null;

    const pct = Math.round(((this.totalHoje - antes) / antes) * 100);
    if (!pct) return {texto: 'igual', sobe: true};

    return {texto: `${Math.abs(pct)}% a ${pct > 0 ? 'mais' : 'menos'}`, sobe: pct > 0};
  }

  get diaComparado(): string {
    const dia = new Date().getDay();
    const nome = DIAS_DA_SEMANA[dia];

    return dia === 0 || dia === 6 ? `${nome} passado` : `${nome} passada`;
  }

  /** Barras por hora, do primeiro ao último horário com venda (no mínimo seis colunas). */
  get barrasHoras(): Array<{altura: number, melhor: boolean, rotulo: string}> {
    const horas = this.horas || [];
    const comVenda = horas.map((valor: number, hora: number) => valor > 0 ? hora : -1).filter((hora: number) => hora >= 0);
    if (!comVenda.length) return [];

    let inicio = comVenda[0], fim = Math.max(comVenda[comVenda.length - 1], new Date().getHours());
    while (fim - inicio < 5) inicio > 0 ? inicio-- : fim++;
    fim = Math.min(23, fim);

    const fatia = horas.slice(inicio, fim + 1);
    const maior = Math.max(...fatia, 1);

    return fatia.map((valor: number, i: number) => ({
      altura: Math.max(6, Math.round((valor / maior) * 100)),
      melhor: valor === maior && valor > 0,
      rotulo: `${inicio + i}h`
    }));
  }

  get melhorHora(): string {
    const barra = this.barrasHoras.find((item: any) => item.melhor);
    return barra ? barra.rotulo : '';
  }

  abraRelatorioVendas(): void {
    this.router.navigateByUrl('/admin/financeiro-delivery-v2').then(() => {});
  }

  // ─────────────────────────── Precisa de você ───────────────────────────

  get toleranciaAtraso(): number {
    return (this.filaAgora && this.filaAgora.minutosTolerancia) || 60;
  }

  /**
   * Todo pedido em aberto, cada um com o próximo passo, mais as pendências que
   * travam a operação. Valor de um pedido é de todos (o operador cobra por ele);
   * o que a permissão de relatórios protege é o agregado do dia.
   */
  get comandas(): ComandaHomeCelular[] {
    const resumo = (this.monitorador.resumoPedidos && this.monitorador.resumoPedidos.value) || {};
    const assinatura = this.pedidosDeAgora.map((p: any) => `${p.id}:${p.statusOrdem}:${p.aceito}:${p.pago}:${p.duracao}`).join('|') +
      `#${resumo.totalErroParceiro}|${resumo.totalContestados}|${this.podeAlterarStatus}`;

    if (assinatura === this.assinaturaComandas) return this.comandasEmCache;

    const pedidos = ordeneParaOTrilho(this.pedidosDeAgora, this.toleranciaAtraso)
      .map(item => this.comandaDoPedido(item.pedido, item.etapa, item.atrasado));

    const pendencias: ComandaHomeCelular[] = [];
    const pendencia = (id: string, quantidade: number, singular: string, plural: string, nota: string, rota: string) => {
      if (!quantidade) return;
      pendencias.push({id, etapa: null, aba: 'todos', rotulo: 'Pendência', icone: 'fe-alert-circle', tom: 'pendencia',
        titulo: `${quantidade} ${quantidade === 1 ? singular : plural}`, meta: '', nota, valor: null, tempo: '',
        quente: false, itens: [], maisItens: 0, passo: null, rota});
    };
    // Pix aguardando já vem pedido a pedido; aqui só o que não aparece na lista.
    pendencia('erros', Number(resumo.totalErroParceiro) || 0, 'pedido com erro no parceiro', 'pedidos com erro no parceiro',
      'Confira antes que o cliente reclame.', '/admin/pedidos/erros');
    pendencia('contestados', Number(resumo.totalContestados) || 0, 'pedido contestado', 'pedidos contestados',
      'Responda a contestação no prazo.', '/admin/pedidos/contestados');

    this.assinaturaComandas = assinatura;
    this.comandasEmCache = [...pedidos.filter(c => c.quente), ...pendencias, ...pedidos.filter(c => !c.quente)];

    return this.comandasEmCache;
  }

  /** As abas com contagem; só aparecem as que têm pedido (e "Todos" quando há mais de uma). */
  get abas(): Array<{id: AbaComanda, nome: string, total: number}> {
    const conte = (aba: AbaComanda) => this.comandas.filter(c => c.aba === aba).length;
    const nomes: Array<[AbaComanda, string]> = [['aceitar', 'Aceitar'], ['pix', 'Pix aguardando'], ['preparo', 'Preparo'],
      ['prontos', 'Prontos'], ['rua', 'Na rua'], ['receber', 'A receber']];
    const abas = nomes.map(([id, nome]) => ({id, nome, total: conte(id)})).filter(aba => aba.total > 0);

    // Com uma etapa só, as abas não ajudam a andar: a lista mostra tudo.
    return abas.length > 1 ? [{id: 'todos', nome: 'Todos', total: this.comandas.length}, ...abas] : [];
  }

  get comandasVisiveis(): ComandaHomeCelular[] {
    if (this.abaAtual === 'todos' || !this.abas.some(aba => aba.id === this.abaAtual)) return this.comandas;
    return this.comandas.filter(c => c.aba === this.abaAtual);
  }

  get comandasNaLista(): ComandaHomeCelular[] {
    return this.comandasVisiveis.slice(0, this.limiteLista);
  }

  get comandasForaDaLista(): number {
    return Math.max(0, this.comandasVisiveis.length - this.limiteLista);
  }

  vejaMaisComandas(): void {
    this.limiteLista += LISTA_PASSO;
  }

  escolhaAba(aba: AbaComanda): void {
    this.abaAtual = aba;
    this.limiteLista = LISTA_INICIAL;
  }

  get pedidosDeAgora(): any[] {
    return this.pedidosAbertos.filter((pedido: any) => (Number(pedido.duracao) || 0) < MINUTOS_PEDIDO_ANTIGO);
  }

  /** Esquecidos em aberto (de outro turno ou dia): um aviso só, fora da lista e da contagem. */
  get pedidosAntigos(): {total: number, maisAntigo: string} {
    const antigos = this.pedidosAbertos.filter((pedido: any) => (Number(pedido.duracao) || 0) >= MINUTOS_PEDIDO_ANTIGO);
    const maior = Math.max(0, ...antigos.map((pedido: any) => Number(pedido.duracao) || 0));
    return {total: antigos.length, maisAntigo: this.duracao(maior)};
  }

  /** "agora", "25 min", "2h05", "3 dias". */
  duracao(minutos: number): string {
    if (minutos < 1) return 'agora';
    if (minutos < 60) return `${minutos} min`;
    if (minutos < 24 * 60) return `${Math.floor(minutos / 60)}h${minutos % 60 < 10 ? '0' : ''}${minutos % 60}`;
    const dias = Math.floor(minutos / (24 * 60));
    return `${dias} ${dias === 1 ? 'dia' : 'dias'}`;
  }

  private comandaDoPedido(pedido: any, etapa: EtapaComanda, atrasado: boolean): ComandaHomeCelular {
    const minutos = Number(pedido.duracao) || 0;
    const forma = pedido.formaDeEntrega || (pedido.mesa ? 'Mesa' : 'Balcão');
    const visual: {[etapa: string]: [string, string]} = {
      aceitar: ['Aceitar', 'fe-bell'], pix: ['Pix aguardando', 'fe-credit-card'], novo: ['Novo', 'fe-bell'],
      preparo: ['Em preparo', 'fe-clock'], pronto: ['Pronto', 'fe-package'], rua: ['Na rua', 'fe-truck'], receber: ['A receber', 'fe-dollar-sign']
    };
    const [rotulo, icone] = atrasado ? ['Atrasado', 'fe-alert-triangle'] : visual[etapa];
    const doPedido = (pedido.itens && pedido.itens.length) ? pedido.itens : (this.itensPorPedido[String(pedido.id)] || []);
    const itens = doPedido.filter((item: any) => item && item.descricao);

    return {
      id: String(pedido.id), etapa, aba: abaDaEtapa(etapa), rotulo, icone, pedido,
      tom: atrasado ? 'atraso' : etapa,
      titulo: this.clienteDoPedido(pedido),
      meta: `#${pedido.codigo}${forma ? ', ' + String(forma).toLowerCase() : ''}`,
      nota: this.notaDaComanda(pedido, etapa, atrasado),
      valor: Number(pedido.total) || 0,
      tempo: this.duracao(minutos),
      quente: atrasado,
      itens: itens.slice(0, 4).map((item: any) => ({qtde: this.qtde(item.qtde), descricao: item.descricao, observacao: item.observacao || ''})),
      maisItens: Math.max(0, itens.length - 4),
      passo: proximoPasso(pedido, etapa, this.podeAlterarStatus),
      rota: this.rotaDoPedido(pedido)
    };
  }

  private notaDaComanda(pedido: any, etapa: EtapaComanda, atrasado: boolean): string {
    if (etapa === 'pix') return 'Entra no preparo quando o pagamento cair.';
    if (atrasado) return `Passou dos ${this.toleranciaAtraso} min combinados.`;
    if (etapa === 'receber') return 'Entregue, mas o pagamento ainda não foi marcado.';
    if (pedido.pago) return 'Já pago.';
    return pedido.fiado ? 'Na conta do cliente (fiado).' : 'A receber na entrega.';
  }

  private rotaDoPedido(pedido: any): string {
    return pedido && pedido.guid ? `/admin/pedidos/detalhes/${pedido.guid}?t=${pedido.id}` : '/admin/pedidos';
  }

  private qtde(valor: any): string {
    const numero = Number(valor) || 0;
    return Number.isInteger(numero) ? String(numero) : numero.toLocaleString('pt-BR', {maximumFractionDigits: 3});
  }

  private clienteDoPedido(pedido: any): string {
    const nome = (pedido.cliente && pedido.cliente.nome) || (pedido.contato && pedido.contato.nome);
    if (nome) return nome;
    if (pedido.mesa) return `Mesa ${pedido.mesa.nome || pedido.mesa.numero || ''}`.trim();
    return 'Balcão';
  }

  /** Itens dos pedidos da lista que ainda não vieram, no máximo 20 por vez (os mais urgentes primeiro). */
  private carregueItens(): void {
    const faltando = ordeneParaOTrilho(this.pedidosDeAgora, this.toleranciaAtraso)
      .map(item => item.pedido)
      .filter((pedido: any) => pedido.guid && !(pedido.itens && pedido.itens.length) &&
        !this.itensPorPedido[String(pedido.id)] && !this.buscandoItens.has(String(pedido.id)))
      .slice(0, 20);

    for (const pedido of faltando) {
      const id = String(pedido.id);
      this.buscandoItens.add(id);
      this.pedidosService.obtenhaPorCodigo(pedido.guid).then((completo: any) => {
        this.itensPorPedido[id] = (completo && completo.itens) || [];
        this.assinaturaComandas = '';
      }).catch(() => {}).finally(() => this.buscandoItens.delete(id));
    }
  }

  /** Mesmo registro da tela de pedidos: agir no pedido é tê-lo visto, e o contador de novos da casca baixa. */
  private marqueVisualizado(pedido: any): void {
    if (!pedido || !pedido.id || pedido.visualizado) return;

    this.pedidosService.foiVisualizado(pedido).then(() => {
      pedido.visualizado = true;
      this.monitorador.atualizeFoiLido(pedido);
    }).catch(() => {});
  }

  abraComanda(comanda: ComandaHomeCelular): void {
    this.marqueVisualizado(comanda.pedido);
    this.router.navigateByUrl(comanda.rota, comanda.pedido ? {state: comanda.pedido} : undefined).then(() => {});
  }

  identifiqueComanda(indice: number, comanda: ComandaHomeCelular): string {
    return comanda.tom + comanda.id;
  }

  estaProcessando(comanda: ComandaHomeCelular): boolean {
    return !!comanda.pedido && this.processando === comanda.pedido.id;
  }

  // ─────────────────────── Ações na comanda ───────────────────────
  // Copiam o mudar-status e o detalhe do pedido: mesmo aviso ao cliente (ligado,
  // menos no balcão), mesma impressão automática, mesmo "Foi pago?" e pontos.

  /** Botão principal: o próximo passo da etapa. */
  executePasso(comanda: ComandaHomeCelular): void {
    const passo = comanda.passo;
    if (!passo || !comanda.pedido || this.processando) return;

    this.marqueVisualizado(comanda.pedido);

    if (passo.tipo === 'aceitar') {
      this.aceite(comanda.pedido);
    } else if (passo.tipo === 'pagar') {
      this.pergunteOPagamento(comanda.pedido, null);
    } else {
      this.vaParaStatus(comanda.pedido, passo.status);
    }
  }

  /** "Saiu" pergunta o entregador (se houver cadastro); "Entregue" pergunta se recebeu (como o "Foi pago?"). */
  vaParaStatus(pedido: any, status: string): void {
    this.folha = '';

    if (status === 'Saiu para entrega') {
      this.carregueEntregadores().then((entregadores: any[]) => {
        if (!entregadores.length) return this.mudeStatus(pedido, status, {});
        this.pedidoEmAcao = pedido;
        this.folha = 'entregador';
      });
      return;
    }

    if (status === 'Entregue' && perguntaPagamento(pedido)) {
      this.pergunteOPagamento(pedido, status);
      return;
    }

    this.mudeStatus(pedido, status, {});
  }

  escolhaEntregador(entregador: any): void {
    const pedido = this.pedidoEmAcao;
    this.folha = '';
    if (pedido) this.mudeStatus(pedido, 'Saiu para entrega', {entregador});
  }

  private pergunteOPagamento(pedido: any, status: string | null): void {
    this.pedidoEmAcao = pedido;
    this.statusEmAcao = status;
    this.escolhendoFormaDoSaldo = false;
    this.folha = 'pagamento';
  }

  /** Resposta da folha de pagamento. Parcial pede a forma do saldo, como a tela de hoje. */
  respondaPagamento(recebeu: boolean, forma: any = null): void {
    const pedido = this.pedidoEmAcao;
    if (!pedido) return;

    if (recebeu && pedido.pagamentoParcial && !forma) {
      this.escolhendoFormaDoSaldo = true;
      return;
    }

    this.folha = '';

    if (this.statusEmAcao) {
      this.mudeStatus(pedido, this.statusEmAcao, recebeu ? {pago: true, forma} : {});
    } else if (recebeu) {
      this.marquePago(pedido, forma);
    }
  }

  /** Formas para o saldo de pagamento parcial: as mesmas que o mudar-status oferece. */
  get formasDoSaldo(): any[] {
    return ((this.empresa && this.empresa.formasDePagamento) || []).filter((forma: any) => !forma.online && !forma.fiado);
  }

  get textoPagamento(): string {
    const pedido = this.pedidoEmAcao;
    if (!pedido) return '';
    const valor = pedido.pagamentoParcial && pedido.saldoPendente ? pedido.saldoPendente : pedido.total;
    const forma = this.formaDoPedido(pedido);
    return `Recebeu ${this.moeda(valor)}${forma ? ' em ' + forma.toLowerCase() : ''}?`;
  }

  private formaDoPedido(pedido: any): string {
    const pagamento = (pedido.pagamentos || [])[0];
    const forma = pagamento && (pagamento.formaDePagamento || pagamento.forma);
    return (forma && (forma.descricao || forma.nome)) || (typeof pagamento?.descricao === 'string' ? pagamento.descricao : '');
  }

  private async aceite(pedido: any): Promise<void> {
    this.processando = pedido.id;

    try {
      const resposta: any = await this.pedidosService.aceitePedido(pedido);
      pedido.aceito = true;
      this.mostreAviso(resposta && resposta.erroNotificar ? `Aceito, mas: ${resposta.erroNotificar}` : 'Pedido aceito.');
      await this.imprima(pedido);
    } catch (erro) {
      this.mostreAviso(this.mensagemDeErro(erro, 'Não foi possível aceitar. Tente de novo.'));
    } finally {
      this.processando = null;
      this.assinaturaComandas = '';
      this.carreguePedidos();
    }
  }

  private async mudeStatus(pedido: any, status: string, extras: {entregador?: any, pago?: boolean, forma?: any}): Promise<void> {
    const codigo = CODIGO_DO_STATUS[status];
    if (codigo === undefined) return;

    const eraNovo = Number(pedido.statusOrdem) === EnumStatusPedido.Novo;
    this.processando = pedido.id;

    try {
      const resposta: any = await this.pedidosService.altereStatus(pedido, codigo, !pedido.balcao);
      pedido.statusOrdem = codigo;
      pedido.status = status;
      let avisoExtra = resposta && resposta.erroNotificar ? ` Aviso ao cliente: ${resposta.erroNotificar}` : '';

      if (codigo === EnumStatusPedido.EmPreparacao && this.empresa && this.empresa.aceitarPedidoAutomatico &&
          this.empresa.configImpressao && this.empresa.configImpressao.momentoImprimirAuto !== EnumMomentoImprimirAuto.NovoPedido) {
        await this.imprima(pedido);
      }

      if (extras.entregador) {
        await this.pedidosService.altereEntregador(pedido, extras.entregador);
        pedido.objetoEntregador = extras.entregador;
      }

      if (eraNovo) this.monitorador.alterouStatusPedido(pedido);

      if (extras.pago) {
        const pontos = await this.marquePago(pedido, extras.forma, true);
        if (pontos) return;
      }

      this.mostreAviso(`#${pedido.codigo}: ${status.toLowerCase()}.${avisoExtra}`);
    } catch (erro) {
      this.mostreAviso(this.mensagemDeErro(erro, 'Não foi possível mudar o status. Tente de novo.'));
    } finally {
      this.processando = null;
      this.assinaturaComandas = '';
      this.carreguePedidos();
    }
  }

  /** Marca como pago; se a loja pontua no pagamento, segue para lançar os pontos (como a tela de hoje). */
  private async marquePago(pedido: any, forma: any, dentroDeOutraAcao = false): Promise<boolean> {
    if (!dentroDeOutraAcao) this.processando = pedido.id;

    try {
      const resposta: any = await this.pedidosService.altereFoiPago(pedido, true, forma);
      pedido.pago = true;

      if (resposta && resposta.redirectAdicionarPontos) {
        await this.adicionePontos(pedido.cliente || pedido.contato, pedido.subvalor);
        return true;
      }

      if (!dentroDeOutraAcao) this.mostreAviso('Pagamento registrado.');
      else this.mostreAviso(`#${pedido.codigo}: entregue e pago.`);
      return false;
    } catch (erro) {
      this.mostreAviso(this.mensagemDeErro(erro, 'Não foi possível registrar o pagamento.'));
      return false;
    } finally {
      if (!dentroDeOutraAcao) {
        this.processando = null;
        this.assinaturaComandas = '';
        this.carreguePedidos();
      }
    }
  }

  /** Mesmo caminho do mudar-status: cliente com cartão vai lançar pontos; sem cartão, cadastrar. */
  private async adicionePontos(contato: any, valor: number): Promise<void> {
    let completo = contato;
    const telefone = this.telefoneCompleto(contato);

    if (telefone) {
      try { completo = (await this.contatosService.obtenhaContato(telefone)) || contato; } catch (e) {}
    }

    const state: any = {contato: completo, valor};
    const urlRetorno = this.router.url;

    if (completo && completo.cartoes && completo.cartoes.length) {
      if (completo.cartoes.length === 1) state.cartao = completo.cartoes[0];
      await this.router.navigateByUrl('/admin/contatos/pontos/adicionar?urlRetorno=' + urlRetorno, {state});
    } else {
      await this.router.navigateByUrl('/admin/contatos/novo?urlRetorno=' + urlRetorno, {state});
    }
  }

  private telefoneCompleto(contato: any): string | null {
    if (!contato) return null;
    if (contato.codigoPais && contato.telefone) return String(contato.codigoPais) + String(contato.telefone);
    if (typeof contato.telefone === 'string' && contato.telefone.indexOf('+') === 0) return contato.telefone;
    return null;
  }

  /** Impressão automática com o pedido completo (as categorias decidem as impressoras). */
  private async imprima(pedido: any): Promise<void> {
    if (!pedido || !pedido.guid || !this.empresa) return;

    try {
      const completo: any = await this.pedidosService.obtenhaPorGuid(pedido.guid);
      if (!completo || !completo.id) return;

      const tela = new TelaImprimirPedido(this.impressao, this.pedidosService, this.nfce);
      tela.empresa = this.empresa;
      tela._pedido = completo;
      await tela.envieImpressaoAutomatica();
    } catch (erro) {
      // Sem impressora (celular sem QZ Tray): o pedido segue, como no detalhe.
    }
  }

  // ── "Mais" ──

  abraMais(comanda: ComandaHomeCelular): void {
    if (!comanda.pedido) return this.abraComanda(comanda);
    this.marqueVisualizado(comanda.pedido);
    this.pedidoEmAcao = comanda.pedido;
    this.etapaEmAcao = comanda.etapa;
    this.confirmandoRecusa = false;
    this.folha = 'mais';
  }

  /** Status para onde o pedido pode ir, menos o atual (cancelar continua no detalhe, que pede motivo). */
  get statusDaFolhaMais(): string[] {
    const pedido = this.pedidoEmAcao;
    if (!pedido || this.etapaEmAcao === 'aceitar') return [];
    return statusPermitidos(pedido, this.podeAlterarStatus).filter(status => CODIGO_DO_STATUS[status] !== Number(pedido.statusOrdem));
  }

  get podeRecusarAqui(): boolean {
    return this.etapaEmAcao === 'aceitar' && this.podeCancelarPedido;
  }

  /** Recusa direta só para pedido próprio; integração pede o motivo da plataforma, que está no detalhe. */
  async recuse(): Promise<void> {
    const pedido = this.pedidoEmAcao;
    if (!pedido) return;

    if (pedido.origemExterna) {
      this.folha = '';
      this.abraRota(this.rotaDoPedido(pedido));
      return;
    }

    if (!this.confirmandoRecusa) {
      this.confirmandoRecusa = true;
      return;
    }

    this.folha = '';
    this.processando = pedido.id;

    try {
      const resposta: any = await this.pedidosService.rejeitePedido(pedido);
      if (resposta && resposta.aguardandoPdv) {
        this.abraRota(this.rotaDoPedido(pedido));
        return;
      }
      this.mostreAviso(`Pedido #${pedido.codigo} recusado.`);
    } catch (erro) {
      this.mostreAviso(this.mensagemDeErro(erro, 'Não foi possível recusar. Tente de novo.'));
    } finally {
      this.processando = null;
      this.assinaturaComandas = '';
      this.carreguePedidos();
    }
  }

  abraPedidoEmAcao(): void {
    const pedido = this.pedidoEmAcao;
    this.folha = '';
    if (pedido) this.router.navigateByUrl(this.rotaDoPedido(pedido), {state: pedido}).then(() => {});
  }

  private carregueEntregadores(): Promise<any[]> {
    if (this.entregadores) return Promise.resolve(this.entregadores);

    return this.entregadorService.liste().then((lista: any) => {
      this.entregadores = (lista || []).filter((entregador: any) => entregador && entregador.ativo !== false);
      return this.entregadores;
    }).catch(() => []);
  }

  private mensagemDeErro(erro: any, padrao: string): string {
    if (typeof erro === 'string' && erro) return erro;
    return (erro && (erro.message || erro.mensagem)) || padrao;
  }

  // ─────────────────────────── Agora na loja ───────────────────────────

  get exibirCaixa(): boolean {
    return this.podeOperarCaixa && this.caixa !== undefined;
  }

  get exibirMesas(): boolean {
    return this.temModuloMesas && !!this.mesas;
  }

  pausarLoja(): void {
    this.casca.abraModalFecharLoja();
  }

  reabrirLoja(): void {
    if (this.reabrindo) return;
    this.reabrindo = true;

    this.empresasService.abra().then(() => {
      this.empresa.fechadoTemporariamente = false;
      delete this.empresa.mensagemPausaProgramada;
      this.mostreAviso('Loja reaberta.');
      this.recarregueEmpresa();
    }).catch(() => this.mostreAviso('Não foi possível reabrir. Tente de novo.'))
      .finally(() => this.reabrindo = false);
  }

  abraRota(rota: string): void {
    this.router.navigateByUrl(rota).then(() => {});
  }

  // ─────────────────────────── Vender e funções ───────────────────────────

  get portasDeVenda(): Array<{funcao: FuncaoMenuV2, nome: string, detalhe: string, icone: string, cor: string}> {
    return PORTAS_DE_VENDA
      .map(porta => ({...porta, funcao: this.funcoes.find((f: FuncaoMenuV2) => f.id === porta.id)}))
      // O catálogo libera o PDV de balcão pelo recurso da loja; vender no balcão é operar o caixa.
      .filter(porta => !!porta.funcao && (porta.id !== 'pedidos/pdv/novo' || this.podeOperarCaixa))
      .map(porta => porta.id === 'pedidos-mesas' && this.exibirMesas
        ? {...porta, detalhe: `${this.mesas.ocupadas} de ${this.mesas.total} ocupadas`} : porta);
  }

  /** Os mais usados neste aparelho; sem histórico, as telas principais do catálogo. */
  get atalhos(): FuncaoMenuV2[] {
    const vender = PORTAS_DE_VENDA.map(porta => porta.id);

    return this.funcoes
      .filter((f: FuncaoMenuV2) => !f.interna && !f.externa && f.grupo !== 'inicio' && f.grupo !== 'conta' && !vender.includes(f.id)
        && (f.acao ? false : !!f.rota) && (!f.somenteBusca || this.uso.pontuacao(f.id) > 0))
      .map((f: FuncaoMenuV2, ordem: number) => ({f, ordem, importancia: f.peso + this.uso.pontuacao(f.id) * 2}))
      .sort((a, b) => b.importancia - a.importancia || a.ordem - b.ordem)
      .slice(0, 8)
      .map(item => item.f);
  }

  corDaFuncao(funcao: FuncaoMenuV2): string {
    return COR_DO_GRUPO[funcao.grupo] || 'cinza';
  }

  /** Ícone do catálogo: `fe-*` direto; o da Mia vem em Font Awesome. */
  iconeDaFuncao(icone: string): string {
    return icone || 'fe-circle';
  }

  /** Mesmo caminho do menu-v2: registra o uso (alimenta os atalhos) e respeita ação, rota ou link externo. */
  execute(funcao: FuncaoMenuV2): void {
    this.monteCatalogo();
    const atual = this.funcoes.find((f: FuncaoMenuV2) => f.id === funcao.id);
    if (!atual) return;

    this.uso.registrar(atual.id);
    this.folha = '';

    if (atual.acao) {
      if (atual.acao === 'abrirLoja') this.casca.abraModalReabrirLoja();
      else if (atual.acao === 'fecharLoja') this.casca.abraModalFecharLoja();
      else if (atual.acao === 'impressao') this.casca.abraModalGerenciarJobs();
      else if (atual.acao === 'sair') this.casca.logout();
      else this.router.navigateByUrl('/admin/pedidos').then(() => {});
    } else if (atual.rota && !atual.externa) {
      this.router.navigateByUrl(atual.rota).then(() => {});
    } else if (atual.rota) {
      window.open(atual.rota, '_blank', 'noopener,noreferrer');
    }
  }

  fecheFolha(): void {
    this.folha = '';
    this.pedidoEmAcao = null;
    this.etapaEmAcao = null;
    this.statusEmAcao = null;
    this.confirmandoRecusa = false;
    this.escolhendoFormaDoSaldo = false;
  }

  @HostListener('document:keydown.escape')
  aoApertarEsc(): void {
    if (this.folha) this.fecheFolha();
  }

  // ─────────────────────────── Divulgar ───────────────────────────

  get linkCardapio(): string {
    return (this.empresa && this.empresa.linkLoja) || '';
  }

  get linkCurto(): string {
    return this.linkCardapio.replace(/^https?:\/\//, '').replace(/\/$/, '');
  }

  compartilhe(): void {
    if (!this.linkCardapio) return;

    const texto = `Peça pelo nosso cardápio: ${this.linkCardapio}`;
    const nav: any = navigator;

    if (nav.share) {
      nav.share({title: this.empresa.nome, text: 'Peça pelo nosso cardápio:', url: this.linkCardapio}).catch(() => {});
      return;
    }

    window.open('https://wa.me/?text=' + encodeURIComponent(texto), '_blank', 'noopener,noreferrer');
  }

  copieLink(mensagem = 'Link copiado.'): void {
    if (!this.linkCardapio) return;

    const area: any = navigator && (navigator as any).clipboard;
    const copie = area && area.writeText ? area.writeText(this.linkCardapio) : Promise.reject();

    copie.then(() => this.mostreAviso(mensagem)).catch(() => this.mostreAviso(this.linkCurto));
  }

  abraLoja(): void {
    if (this.linkCardapio) window.open(this.linkCardapio, '_blank', 'noopener,noreferrer');
  }

  verHomeCompletaAgora(): void {
    this.verHomeCompleta.emit();
  }

  // ─────────────────────────── Utilitários ───────────────────────────

  mostreAviso(texto: string): void {
    this.aviso = texto;
    if (this.timerAviso) clearTimeout(this.timerAviso);
    this.timerAviso = setTimeout(() => this.aviso = '', 2400);
  }

  moeda(valor: any): string {
    return (Number(valor) || 0).toLocaleString('pt-BR', {style: 'currency', currency: 'BRL'});
  }

  /** "684,30" sem o símbolo: o "R$" vai menor ao lado. */
  valorSemSimbolo(valor: any): string {
    return (Number(valor) || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2});
  }

  hora(horario: any): string {
    if (!horario) return '';
    const data = new Date(horario);
    return isNaN(data.getTime()) ? '' : data.toTimeString().substr(0, 5);
  }

  clienteAgendado(pedido: any): string {
    return this.clienteDoPedido(pedido);
  }

  private ehHoje(horario: any): boolean {
    if (!horario) return false;
    const data = new Date(horario);
    return !isNaN(data.getTime()) && data.toDateString() === new Date().toDateString();
  }

  private diaNoFiltro(data: Date): string {
    const dois = (n: number) => (n < 10 ? '0' : '') + n;
    return `${data.getFullYear()}${dois(data.getMonth() + 1)}${dois(data.getDate())}`;
  }

  private minutosDoDia(data: Date): number {
    return data.getHours() * 60 + data.getMinutes();
  }

  private minutosDaHora(hora: string): number {
    return Number(String(hora).substr(0, 2)) * 60 + Number(String(hora).substr(3, 2));
  }
}
